import { useState, type FormEvent } from 'react';
import { Decimal, formaterPourcentage, validerBenchmark } from '@gsm/partage';
import { useRessource, messageErreur } from '../../api/ressource';
import { api, ErreurApi } from '../../api/client';
import { useAuth } from '../../auth/contexte';
import { Bloc, Tableau, type Colonne } from '../../composants/Tableau';
import { BandeauAlerte } from '../../composants/BandeauAlerte';
import { Champ } from '../../composants/Champ';
import { Pastille } from '../../composants/Pastille';
import { Nombre } from '../../composants/Nombre';
import { Modale } from '../../composants/Modale';
import { Icone } from '../../composants/Icone';

interface Profil {
  id: string;
  code: string;
  libelle: string;
  description: string | null;
  charia: boolean;
  tamponTresoreriePct: string;
  tauxFraisAnnuelPct: string;
  actif: boolean;
  benchmark: { poidsPct: string; indice: { code: string; libelle: string } }[];
}

interface Indice {
  id: string;
  code: string;
  libelle: string;
}

const VIDE = {
  code: '',
  libelle: '',
  description: '',
  charia: false,
  tamponTresoreriePct: '1',
  tauxFraisAnnuelPct: '1.5',
  actif: true,
};

export function Profils() {
  const { aRole } = useAuth();
  const { donnees, chargement, erreur, recharger } = useRessource<Profil[]>('/profils');
  const indices = useRessource<Indice[]>('/indices');
  const profils = donnees ?? [];
  const peutCreer = aRole('ADMIN', 'RESP_GESTION');

  const [edition, setEdition] = useState<string | null>(null);
  const [saisie, setSaisie] = useState(VIDE);
  const [composantes, setComposantes] = useState<{ indiceCode: string; poidsPct: string }[]>([]);
  const [erreurForm, setErreurForm] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);

  const enCreation = edition === '';

  const champ = (cle: keyof typeof VIDE) => ({
    value: String(saisie[cle]),
    onChange: (e: { target: { value: string } }) => setSaisie({ ...saisie, [cle]: e.target.value }),
  });

  function ouvrirCreation() {
    setSaisie(VIDE);
    setComposantes([]);
    setEdition('');
    setErreurForm(null);
  }
  function ouvrirEdition(p: Profil) {
    setSaisie({
      code: p.code,
      libelle: p.libelle,
      description: p.description ?? '',
      charia: p.charia,
      tamponTresoreriePct: new Decimal(p.tamponTresoreriePct).toString(),
      tauxFraisAnnuelPct: new Decimal(p.tauxFraisAnnuelPct).toString(),
      actif: p.actif,
    });
    setComposantes(
      p.benchmark.map((c) => ({
        indiceCode: c.indice.code,
        poidsPct: new Decimal(c.poidsPct).toString(),
      })),
    );
    setEdition(p.id);
    setErreurForm(null);
  }
  function fermer() {
    setEdition(null);
  }

  // « Somme du benchmark = 100 % » contrôlée en direct, avec la fonction
  // partagée : l'écran refuse ce que le serveur refuserait, sans divergence.
  const erreurBenchmark =
    composantes.length > 0 && composantes.every((c) => c.indiceCode && c.poidsPct)
      ? validerBenchmark(composantes)
      : composantes.length === 0
        ? 'Ajoutez au moins un indice de référence.'
        : null;

  async function enregistrer(evenement: FormEvent) {
    evenement.preventDefault();
    setErreurForm(null);
    setOccupe(true);
    try {
      if (enCreation) {
        await api.post('/profils', {
          code: saisie.code,
          libelle: saisie.libelle,
          description: saisie.description || undefined,
          charia: saisie.charia,
          tamponTresoreriePct: saisie.tamponTresoreriePct,
          tauxFraisAnnuelPct: saisie.tauxFraisAnnuelPct,
          benchmark: composantes,
        });
      } else {
        // Le code d'un profil est immuable : l'API ne l'accepte pas en révision.
        await api.patch(`/profils/${edition}`, {
          libelle: saisie.libelle,
          description: saisie.description || undefined,
          charia: saisie.charia,
          tamponTresoreriePct: saisie.tamponTresoreriePct,
          tauxFraisAnnuelPct: saisie.tauxFraisAnnuelPct,
          actif: saisie.actif,
          benchmark: composantes,
        });
      }
      fermer();
      await recharger();
    } catch (e) {
      setErreurForm(e instanceof ErreurApi ? messageErreur(e) : 'Enregistrement impossible.');
    } finally {
      setOccupe(false);
    }
  }

  const colonnes: Colonne<Profil>[] = [
    { cle: 'code', entete: 'Code', num: true, rendu: (p) => p.code },
    { cle: 'libelle', entete: 'Profil', rendu: (p) => p.libelle },
    {
      cle: 'charia',
      entete: 'Charia',
      rendu: (p) => (p.charia ? <Pastille ton="interface">Conforme</Pastille> : '—'),
    },
    {
      cle: 'tampon',
      entete: 'Tampon trésorerie',
      num: true,
      rendu: (p) => <Nombre valeur={p.tamponTresoreriePct} nature="pourcentage" />,
    },
    {
      cle: 'frais',
      entete: 'Frais annuels',
      num: true,
      rendu: (p) => <Nombre valeur={p.tauxFraisAnnuelPct} nature="pourcentage" />,
    },
    {
      cle: 'benchmark',
      entete: 'Benchmark composite',
      rendu: (p) =>
        p.benchmark
          .map((c) => `${c.indice.code} ${formaterPourcentage(c.poidsPct, 0)}`)
          .join(' · ') || '—',
    },
    ...(peutCreer
      ? [
          {
            cle: 'actions',
            entete: '',
            num: true,
            rendu: (p: Profil) => (
              <button
                type="button"
                className="tableau__action"
                onClick={() => ouvrirEdition(p)}
                aria-label={`Éditer ${p.libelle}`}
              >
                <Icone nom="editer" taille={16} />
              </button>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <p className="entete__fil">Référentiels</p>
      <div className="entete">
        <h1>Profils de gestion</h1>
        <span className="entete__ref">{profils.length} profils</span>
      </div>

      {erreur && (
        <BandeauAlerte nature="refus" titre="Chargement impossible">
          {messageErreur(erreur)}
        </BandeauAlerte>
      )}

      <Bloc
        titre="Profils"
        meta={chargement ? 'chargement…' : undefined}
        actions={
          peutCreer && (
            <button
              type="button"
              className="bouton bouton--icone bouton--compact"
              onClick={ouvrirCreation}
            >
              <Icone nom="creer" taille={15} />
              Nouveau profil
            </button>
          )
        }
      >
        <Tableau colonnes={colonnes} lignes={profils} cle={(p) => p.id} />
      </Bloc>

      {profils.map((p) => (
        <Bloc key={p.id} titre={p.libelle} meta={p.code}>
          {p.description && <p className="consigne consigne--tete">{p.description}</p>}
          <Tableau
            colonnes={[
              { cle: 'indice', entete: 'Indice', rendu: (c) => c.indice.code },
              { cle: 'libelle', entete: 'Intitulé', rendu: (c) => c.indice.libelle },
              {
                cle: 'poids',
                entete: 'Poids',
                num: true,
                rendu: (c) => <Nombre valeur={c.poidsPct} nature="pourcentage" />,
              },
            ]}
            lignes={p.benchmark}
            cle={(c) => c.indice.code}
            vide="Aucun indice de référence."
          />
        </Bloc>
      ))}

      <Modale
        ouverte={edition !== null}
        onFermer={fermer}
        surtitre="Profil de gestion"
        titre={enCreation ? 'Nouveau profil' : 'Modifier le profil'}
        taille="large"
      >
        {erreurForm && (
          <BandeauAlerte nature="refus" titre="Enregistrement refusé">
            {erreurForm}
          </BandeauAlerte>
        )}
        <form onSubmit={(e) => void enregistrer(e)} noValidate>
          <Champ
            label="Code"
            {...champ('code')}
            placeholder="PATRIMOINE"
            required
            disabled={!enCreation}
            aide={enCreation ? undefined : 'Le code d’un profil est immuable.'}
          />
          <Champ label="Libellé" {...champ('libelle')} placeholder="Patrimonial" required />
          <Champ label="Description" {...champ('description')} />
          <div className="champs-en-ligne">
            <Champ
              label="Tampon trésorerie (%)"
              montant
              {...champ('tamponTresoreriePct')}
              aide="Part conservée en liquidités."
            />
            <Champ
              label="Frais annuels (%)"
              montant
              {...champ('tauxFraisAnnuelPct')}
              aide="Commission de gestion."
            />
          </div>
          <label className="case-a-cocher">
            <input
              type="checkbox"
              checked={saisie.charia}
              onChange={(e) => setSaisie({ ...saisie, charia: e.target.checked })}
            />
            Profil charia — seuls les instruments conformes seront alloués
          </label>
          {!enCreation && (
            <label className="case-a-cocher">
              <input
                type="checkbox"
                checked={saisie.actif}
                onChange={(e) => setSaisie({ ...saisie, actif: e.target.checked })}
              />
              Profil actif
            </label>
          )}

          <div className="bloc__titre bloc__titre--sous">
            <h2>Benchmark composite</h2>
            <span className="meta">la somme des poids doit valoir 100 %</span>
          </div>

          {composantes.map((c, index) => (
            <div key={index} className="saisie-ligne saisie-ligne--benchmark">
              <select
                className="champ__select"
                value={c.indiceCode}
                onChange={(e) => {
                  const copie = [...composantes];
                  copie[index] = { ...c, indiceCode: e.target.value };
                  setComposantes(copie);
                }}
              >
                <option value="">— indice —</option>
                {(indices.donnees ?? []).map((i) => (
                  <option key={i.id} value={i.code}>
                    {i.code} — {i.libelle}
                  </option>
                ))}
              </select>
              <input
                className="saisie-chiffre"
                aria-label="Poids (%)"
                value={c.poidsPct}
                placeholder="%"
                onChange={(e) => {
                  const copie = [...composantes];
                  copie[index] = { ...c, poidsPct: e.target.value };
                  setComposantes(copie);
                }}
              />
              <button
                type="button"
                className="bouton bouton--fantome bouton--compact"
                onClick={() => setComposantes(composantes.filter((_, i) => i !== index))}
                aria-label="Retirer l’indice"
              >
                ×
              </button>
            </div>
          ))}

          <div className="barre-actions barre-actions--entre">
            <button
              type="button"
              className="bouton bouton--fantome bouton--compact"
              onClick={() => setComposantes([...composantes, { indiceCode: '', poidsPct: '' }])}
            >
              + indice
            </button>
            {erreurBenchmark && <span className="message-alerte">{erreurBenchmark}</span>}
          </div>

          <div className="modale__actions">
            <button type="button" className="bouton bouton--fantome" onClick={fermer}>
              Annuler
            </button>
            <button
              className="bouton"
              type="submit"
              disabled={occupe || !saisie.code || !saisie.libelle || erreurBenchmark !== null}
            >
              {occupe ? 'Enregistrement…' : enCreation ? 'Créer le profil' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </Modale>
    </>
  );
}
